import React, { createContext, useContext, useState, useEffect } from 'react';

// Contexto para gerenciar o estado global da aplicação
const AppContext = createContext();

export const AppProvider = ({ children }) => {
  // Carrinho de produtos
  const [cartProducts, setCartProducts] = useState([]);

  // Adiciona produto ao carrinho (se já existe, incrementa quantidade)
  const addProductToCart = (product) => {
    setCartProducts((prev) => {
      return prev.map((p) =>
        p.id === product.id
          ? { ...p, quantity: (p.quantity || 1) + 1 }
          : p
      ).concat(
        prev.some((p) => p.id === product.id)
          ? []
          : [{ ...product, quantity: 1 }]
      );
    });
  };

  // Remove um produto do carrinho (decrementa quantidade ou remove)
  const removeProductFromCart = (productId) => {
    setCartProducts((prev) => prev.filter(p => p.id !== productId));
  };

  // Diminui a quantidade de um produto no carrinho sem removê-lo
  const decreaseProductQuantity = (productId) => {
    setCartProducts((prev) => {
      return prev.map((p) =>
        p.id === productId
          ? { ...p, quantity: Math.max((p.quantity || 1) - 1, 1) }
          : p
      );
    });
  };

  // Limpa o carrinho de produtos
  const clearCartProducts = () => setCartProducts([]);

  // Estado para controlar o atendimento ativo
  const [atendimentoId, setAtendimentoId] = useState(null);
  // Estado para controlar o usuário atual (cliente)
  const [currentUser, setCurrentUser] = useState(null);
  
  // Estado para controlar se o usuário é mensalista
  const [isMensalista, setIsMensalista] = useState(false);
  
  // Estado para controlar se o usuário é administrador
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Estado para controlar a agenda
  const [schedule, setSchedule] = useState([]);
  
  // Estado para controlar o tempo de inatividade
  const [idleTime, setIdleTime] = useState(0);
  // Tempo máximo de inatividade em milissegundos (1 minuto)
  const MAX_IDLE_TIME = 60000;
  
  // Função para verificar se um cliente é mensalista
  const checkMensalista = (name) => {
    // Lista simulada de mensalistas
    const mensalistas = [
      'Gabriel Ramos',
      'João Silva',
      'Maria Oliveira',
      'Pedro Santos',
      'Ana Costa'
    ];
    
    return mensalistas.includes(name);
  };
  
  // Função para fazer login de um cliente
  const loginUser = (user, admin = false) => {
    // Se o usuário tiver uma foto_url, incluímos ela no objeto
    const userWithPhoto = user.foto_url ? {
      ...user,
      photoUrl: user.foto_url // Normalizando o nome do campo
    } : user;
    
    setCurrentUser(userWithPhoto);
    const mensalista = admin ? false : checkMensalista(user.name);
    setIsMensalista(mensalista);
    setIsAdmin(admin);
    resetIdleTime();
  };
  
  // Função para fazer logout de um cliente
  const logoutUser = () => {
    setAtendimentoId(null); // Limpa atendimento ativo ao fazer logout
    setCurrentUser(null);
    setIsMensalista(false);
    setIsAdmin(false);
  };
  
  // Função para resetar o tempo de inatividade
  const resetIdleTime = () => {
    setIdleTime(0);
  };
  
  // Efeito para controlar o tempo de inatividade
  useEffect(() => {
    let interval;
    if (currentUser) {
      interval = setInterval(() => {
        setIdleTime((prevTime) => {
          const newTime = prevTime + 1000;
          return newTime;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentUser]);
  
  // Efeito para resetar o tempo de inatividade quando houver interação
  useEffect(() => {
    const handleActivity = () => {
      if (currentUser) {
        resetIdleTime();
      }
    };
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
    };
  }, []);
  
  // Simulação de carregamento da agenda
  useEffect(() => {
    // Aqui seria a integração com o sistema de scraping do AppBarber
    const fetchSchedule = () => {
      // Dados simulados
      const mockSchedule = [
        { id: 1, clientName: 'Gabriel Ramos', service: 'Corte + Barba', time: '14:00', barber: 'João' },
        { id: 2, clientName: 'Pedro Santos', service: 'Corte', time: '15:00', barber: 'Carlos' },
        { id: 3, clientName: 'Ana Costa', service: 'Hidratação', time: '16:00', barber: 'João' },
        { id: 4, clientName: 'Maria Oliveira', service: 'Corte', time: '16:30', barber: 'Carlos' },
        { id: 5, clientName: 'João Silva', service: 'Barba', time: '17:00', barber: 'João' },
      ];
      
      setSchedule(mockSchedule);
    };
    
    fetchSchedule();
    
    // Atualiza a agenda a cada 5 minutos
    const interval = setInterval(fetchSchedule, 300000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Função para navegar diretamente para o Menu Principal
  const goToMainMenu = (navigate, isFirstSession = false) => {
    if (navigate) {
      navigate('/', { state: { showMainMenu: true, isFirstSession } });
    }
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isMensalista,
        setIsMensalista,
        isAdmin,
        setIsAdmin,
        schedule,
        idleTime,
        MAX_IDLE_TIME,
        loginUser,
        logoutUser,
        resetIdleTime,
        atendimentoId,
        setAtendimentoId,
        goToMainMenu,
        // Carrinho de produtos
        cartProducts,
        addProductToCart,
        removeProductFromCart,
        decreaseProductQuantity,
        clearCartProducts,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
